import { useEffect } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { useAuth } from '@domain/auth/AuthContext';
import { useSession } from '@domain/session/SessionContext';
import { isBrowser } from '@lib/environment/isBrowser';
import { reportMykissClientError } from '@lib/errors/client';
import {
  ACTIVE_FEATURE_FLAGS,
  filterFeatureFlags,
} from '@config/featureFlags/getActiveFeatureFlags';
import { useCurrentURL } from '@hooks/useCurrentURL';
import { localStorageWrapper } from '@lib/localStorage/localStorageWrapper';
import { QueryStringKey } from '@lib/url/queryUtils';

export const ClientSideInitializer = () => {
  const { setInitialClientRenderComplete } = useSession();
  const { checkForTokens } = useAuth();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { removeParameters } = useCurrentURL();

  // This effect is used to read the feature flag query parameter from the URL,
  // store it in browsers local storage, and then remove it from the URL.
  useEffect(() => {
    const flagValues = searchParams.getAll(QueryStringKey.FeatureFlags);
    if (isBrowser() && flagValues.length > 0) {
      const filteredFlagValues = filterFeatureFlags(flagValues);
      localStorageWrapper.setItem(ACTIVE_FEATURE_FLAGS, filteredFlagValues);
      const clearedURL = removeParameters([QueryStringKey.FeatureFlags]);
      try {
        router.replace(clearedURL);
      } catch (error) {
        reportMykissClientError(error);
      }
    }
  }, [searchParams, removeParameters, router]);

  useEffect(() => {
    if (isBrowser()) {
      // Check if the user has valid auth tokens.
      checkForTokens().catch(reportMykissClientError);
    }
  }, [checkForTokens]);

  useEffect(() => {
    if (isBrowser()) {
      setInitialClientRenderComplete(true);
    }
  }, [setInitialClientRenderComplete]);

  return null;
};
